import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { InputRow, NavRow, SegRow } from '../components/Controls'
import { IntakeCard, todayCard, CARD_CSS, type CardData } from '../components/IntakeCard'
import { BrandSheet } from '../components/BrandSheet'
import { useStore } from '../state/store'
import { loadTradeLimits } from '../lib/api'
import { fmt, fmtC, giveCents, pieceName, type Condition, type TradeGive as Give, type TradeLimits } from '../lib/types'

const blank = (): Give => ({ key: String(Date.now()), brand: '', item: '', size: '', cond: 'excellent', credit: '', whose: 'shane', note: '' })

/** One piece the customer hands over: what it is, the credit (its cost from now on) and whose piece it becomes. */
export function TradeGive() {
  const nav = useNav()
  const { trade, setTrade } = useStore()
  const index = (useLocation().state as { index?: number } | null)?.index
  const editing = index != null && trade.give[index] != null
  const [g, setG] = useState<Give>(editing ? trade.give[index!] : blank())
  const [brandSheet, setBrandSheet] = useState(false)
  const [limits, setLimits] = useState<TradeLimits | null>(null)
  useEffect(() => { loadTradeLimits().then(setLimits).catch(() => {}) }, [])
  const set = (patch: Partial<Give>) => setG((x) => ({ ...x, ...patch }))
  const ok = !!g.brand.trim() && !!g.item.trim() && giveCents(g) > 0
  const over = limits ? giveCents(g) > limits.staff_credit_limit_cents : false
  // The only owner default that is safe: the outgoing side has exactly one owner and no consigned piece (never in a trade anyway).
  useEffect(() => {
    if (editing) return
    // Consignify names the owner ('Shane', 'Masih', 'Split'); match on the name when the metafield owner is absent.
    const names = trade.out.map((l) => (l.piece.consignor?.name ?? l.piece.owner ?? '').toLowerCase()).filter(Boolean)
    const owners = Array.from(new Set(names))
    if (owners.length === 1 && ['shane', 'masih', 'split'].includes(owners[0])) set({ whose: owners[0] as Give['whose'] })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps
  const card: CardData = {
    how: 'traded', date: todayCard(), id: '', from: trade.customerName || 'Trade customer', deal: g.credit ? `${fmt(g.credit)} trade credit` : '',
    whose: g.whose, sentence: g.note || 'Trade-in credited against this trade', what: pieceName(g.brand, g.item, g.size), cond: g.cond, intakeNo: 'pending', tradeNo: null,
  }
  const save = () => {
    setTrade((t) => ({ give: editing ? t.give.map((x, i) => i === index ? g : x) : [...t.give, g] }))
    nav('/trade')
  }
  return (
    <Page
      bar={<TopBar back="Trade" backTo="/trade" title={editing ? 'Piece they give' : 'Add a piece they give'} />}
      aside={
        <>
          <div className="aside__body">
            <div className="icard-head"><b>Intake card</b><span>Prints when the trade is paid</span></div>
            <style>{CARD_CSS}</style>
            <div className="ic-preview"><IntakeCard c={card} /></div>
            <div className="hint" style={{ padding: '10px 20px' }}>The credit becomes this piece's cost from now on: credit it as what Starchive would pay to buy it for cash today. Shane prices and lists it later from the pending intake queue.</div>
          </div>
          <div className="aside__foot" style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-secondary" style={{ width: 110 }} onClick={() => nav('/trade')}>Cancel</button>
            <button type="button" className="cta" style={{ flexGrow: 1 }} disabled={!ok} onClick={save}><span>{editing ? 'Save' : 'Add to trade'}</span><span>{g.credit ? fmtC(giveCents(g)) : ''}</span></button>
          </div>
        </>
      }
    >
      <div className="section-title">Piece they're handing over</div>
      <div className="card">
        <NavRow label="Brand" value={g.brand || 'Pick or add'} set={!!g.brand} onClick={() => setBrandSheet(true)} />
        <InputRow id="titem" label="Item" value={g.item} onChange={(item) => set({ item: item.toUpperCase() })} placeholder="WHAT IT IS" caps />
        <InputRow id="tsize" label="Size" value={g.size} onChange={(size) => set({ size })} placeholder="M, 43, 32, OS…" />
        <SegRow<Condition> label="Condition" value={g.cond} options={[{ v: 'nwt', label: 'NWT' }, { v: 'excellent', label: 'Excellent' }, { v: 'good', label: 'Good' }, { v: 'fair', label: 'Fair' }]} onChange={(cond) => set({ cond })} />
      </div>
      <div className="section-title">Credit</div>
      <div className="card">
        <InputRow id="tcredit" label="Credit given" value={g.credit ? '$' + Number(g.credit).toLocaleString('en-US') : ''} onChange={(v) => set({ credit: v.replace(/[^0-9]/g, '') })} placeholder="$0" numeric />
        <SegRow<Give['whose']> label="Whose piece" value={g.whose} options={[{ v: 'shane', label: 'Shane' }, { v: 'masih', label: 'Masih' }, { v: 'split', label: 'Split' }]} onChange={(whose) => set({ whose })} />
        <InputRow id="tnote" label="Deal note" value={g.note} onChange={(note) => set({ note })} placeholder="Optional" />
      </div>
      {over ? <div className="banner">This credit is over the {fmtC(limits!.staff_credit_limit_cents)} staff limit. Shane or Masih signs the trade.</div>
        : <div className="hint">{limits ? `Credits over ${fmtC(limits.staff_credit_limit_cents)} need an owner ID to finish the trade.` : ''}</div>}
      {brandSheet && <BrandSheet value={g.brand} onPick={(brand) => { set({ brand }); setBrandSheet(false) }} onClose={() => setBrandSheet(false)} />}
    </Page>
  )
}
